import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Box,
  CSSObject,
  Drawer as MuiDrawer,
  Fade,
  IconButton,
  Button,
  styled,
  Theme,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams, Link } from 'react-router-dom';
import { useStore } from '@/components/store/Store';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { ChildrenProp } from '@/declarations/ChildrenProp';
import Styles from '@/assets/js/Styles';
import { isImportedView } from '@/views/ContentView/importedView';
import Layout from './Layout';
import Header from './Header';
import SiteSelector from './SiteSelector';
import UserAvatarMenu from './UserAvatarMenu';
import MainMenu from './MainMenu';
import Container from './Container';

const drawerWidth = '250px';

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(6)} + 1px)`,
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open ? openedMixin(theme) : closedMixin(theme)),

  '& .MuiDrawer-paper': {
    position: 'static',
    gridArea: 'menu',
    ...(open ? openedMixin(theme) : closedMixin(theme)),
  },
}));

export const MainLayout: FC<
  ChildrenProp & {
    hideTopNav?: boolean;
  }
> = ({ children, hideTopNav = false }) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tAria } = useTranslation('aria');
  const [menuOpen, setMenuOpen] = useState<boolean>(true);
  const location = useLocation();
  const {
    state: { currentUser },
  } = useStore();
  const isSuperAdmin = currentUser?.user?.is_super_admin;
  const m24AdminActive = location.pathname.includes('m24admin');

  useEffect(() => {
    // setMenuOpen(false);
  }, [location]);

  // The view of imported pages is a work list that wants the width: the menu collapses on the way in and opens
  // again on the way out. A menu the user opened meanwhile is left alone.
  const [searchParams] = useSearchParams();
  const showImported = isImportedView(searchParams);
  const collapsedForImported = useRef<boolean>(false);
  useEffect(() => {
    if (showImported) {
      collapsedForImported.current = true;
      setMenuOpen(false);
    } else if (collapsedForImported.current) {
      collapsedForImported.current = false;
      setMenuOpen(true);
    }
  }, [showImported]);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100vw',
        height: '100vh',
        display: 'grid',
        gridTemplateColumns: 'min-content 1fr',
        gridTemplateRows: '100vh',
        gridTemplateAreas: `
          "menu mainLayout"
        `,
      }}>
      <Box sx={{ gridArea: 'mainLayout', position: 'relative' }}>
        <Layout
          headerContent={
            !hideTopNav && (
              <Header
                additionalContentRight={
                  <>
                    {isSuperAdmin && (
                      <Button
                        variant={m24AdminActive ? 'contained' : 'text'}
                        component={Link}
                        to='/m24admin'
                        startIcon={<MaterialSymbol name='settings' />}
                        sx={{ marginRight: 2, backgroundColor: Styles.Colors.SUPERADMIN_BG }}>
                        Museum24 ADMIN
                      </Button>
                    )}
                    <SiteSelector />
                    <UserAvatarMenu />
                  </>
                }
                hideMenuButton
              />
            )
          }>
          {children}
        </Layout>
      </Box>
      <Drawer open={menuOpen} variant='permanent'>
        <Container pt={1} pl={2} pr={0.5} right fullWidth>
          <Fade in={menuOpen}>
            <Typography sx={{ flex: 1 }} variant='button'>
              {tCommon('menu')}
            </Typography>
          </Fade>
          <IconButton
            aria-label={tAria('components.MainLayout.ToggleMenu')}
            sx={(theme) => ({ color: theme.palette.text.primary })}
            onClick={() => setMenuOpen((o) => !o)}>
            {menuOpen ? <MaterialSymbol name='menu_open' /> : <MaterialSymbol name='menu' />}
          </IconButton>
        </Container>
        <MainMenu open={menuOpen} toggleMenuOpen={() => setMenuOpen(!menuOpen)} />
      </Drawer>
    </Box>
  );
};

export default MainLayout;
